/** @format */

import React, { useState } from 'react';
import {
  Card,
  Col,
  Row,
  Input,
  Button,
  message,
  Select,
  Dropdown,
  Radio,
  Menu,
  DatePicker,
} from 'antd';
import moment from 'moment';
import plus from 'img/Plus.svg';
import { Customer, PlanData } from '../types/customer';

interface CustomerPlansProps {
  customer: Customer;
  onUpdate?: (updateData: Partial<Customer>) => Promise<void>;
}

interface EditState {
  ancillaries: boolean[];
  privateMed: boolean;
  majorMed: boolean;
}

interface EditableData {
  ancillaries: PlanData[];
  privateMed: PlanData;
  majorMed: PlanData;
}

const statusOptions = [
  'Paid',
  'Pending',
  'Cancelled',
  'Declined',
  'In Progress',
  'Missing Info/TLO',
  'Three-Way Call required',
  'Done Pending Payment',
  'Contact Required',
  'SRE',
];

const CustomerPlans: React.FC<CustomerPlansProps> = ({ customer, onUpdate }) => {
  const [customerData, setCustomerData] = useState<Customer>(customer);

  const [editState, setEditState] = useState<EditState>({
    ancillaries: customerData.ancillaries?.map(() => false) || [],
    privateMed: false,
    majorMed: false,
  });

  const [editableData, setEditableData] = useState<EditableData>({
    ancillaries: customerData.ancillaries || [],
    privateMed: customerData.privateMed || {},
    majorMed: customerData.majorMed || {
      carrier: '',
      billingDate: '',
      premium: '',
      status: '',
    },
  });

  const toggleEditMode = (planType: keyof EditState, index: number | null = null) => {
    setEditState((prevState) => {
      if (index !== null && planType === 'ancillaries') {
        const newAncillariesEditState = [...prevState.ancillaries];
        newAncillariesEditState[index] = !newAncillariesEditState[index];
        return { ...prevState, ancillaries: newAncillariesEditState };
      } else {
        return { ...prevState, [planType]: !prevState[planType] };
      }
    });
  };

  const handleSave = async (planType: keyof EditableData, index: number | null = null) => {
    try {
      let updatePayload: Partial<Customer> = {};

      if (index !== null && planType === 'ancillaries') {
        const updatedAncillaries = [...editableData.ancillaries];
        updatePayload = { ancillaries: updatedAncillaries };
      } else {
        updatePayload = { [planType]: editableData[planType] };
      }

      if (onUpdate) {
        await onUpdate(updatePayload);
      }

      message.success(`${planType} updated successfully!`);
      setCustomerData((prevData) => ({ ...prevData, ...updatePayload }));
      toggleEditMode(planType, index);
    } catch (error) {
      message.error(`Failed to update ${planType}`);
    }
  };

  const cancelEdit = (planType: keyof EditableData, index: number | null = null) => {
    if (index !== null && planType === 'ancillaries') {
      setEditableData((prevData) => {
        const newAncillaries = [...prevData.ancillaries];
        if (!customerData.ancillaries?.[index]) {
          newAncillaries.splice(index, 1);
          setEditState((prevEditState) => {
            const newAncillariesEditState = [...prevEditState.ancillaries];
            newAncillariesEditState.splice(index, 1);
            return { ...prevEditState, ancillaries: newAncillariesEditState };
          });
        } else {
          newAncillaries[index] = { ...customerData.ancillaries[index] };
        }
        return { ...prevData, ancillaries: newAncillaries };
      });
    } else {
      setEditableData((prevData) => ({
        ...prevData,
        [planType]: customerData[planType] || {},
      }));
    }
    toggleEditMode(planType, index);
  };

  const handleChange = (
    planType: keyof EditableData,
    index: number | null,
    field: string,
    value: any
  ) => {
    setEditableData((prevData) => {
      if (index !== null && planType === 'ancillaries') {
        const newAncillaries = [...prevData.ancillaries];
        newAncillaries[index] = { ...newAncillaries[index], [field]: value };
        return { ...prevData, ancillaries: newAncillaries };
      } else {
        return {
          ...prevData,
          [planType]: { ...prevData[planType], [field]: value },
        };
      }
    });
  };

  const getStatusStyle = (status?: string) => {
    switch (status) {
      case 'Paid':
        return { backgroundColor: 'green', color: 'white', padding: '1px 4px' };
      case 'Pending':
        return { backgroundColor: 'yellow', color: 'black', padding: '1px 4px' };
      case 'Declined':
        return { backgroundColor: 'red', color: 'white', padding: '1px 4px' };
      case 'Cancelled':
        return { backgroundColor: 'gray', color: 'white', padding: '1px 4px' };
      case 'Missing Info/TLO':
        return { backgroundColor: 'yellow', color: 'black', padding: '1px 4px' };
      case 'Contact Required':
        return { backgroundColor: 'red', color: 'white', padding: '1px 4px' };
      case 'SRE':
        return { backgroundColor: 'yellow', color: 'black', padding: '1px 4px' };
      default:
        return { backgroundColor: 'red', color: 'white', padding: '1px 4px' };
    }
  };

  const handleAddPlan = (planType: keyof EditableData) => {
    if (planType === 'ancillaries') {
      setEditableData((prevData) => ({
        ...prevData,
        ancillaries: [
          ...prevData.ancillaries,
          { carrier: '', billingDate: '', premium: '', status: '' },
        ],
      }));
      setEditState((prevState) => ({
        ...prevState,
        ancillaries: [...prevState.ancillaries, true],
      }));
    } else if (planType === 'privateMed') {
      setEditableData((prevData) => ({
        ...prevData,
        privateMed: { carrier: '', billingDate: '', premium: '', status: '' },
      }));
      toggleEditMode('privateMed');
    } else if (planType === 'majorMed') {
      setEditableData((prevData) => ({
        ...prevData,
        majorMed: { carrier: '', billingDate: '', premium: '', status: '' },
      }));
      toggleEditMode('majorMed');
    }
    message.success(`${planType} plan added successfully!`);
  };

  const planMenu = (
    <Menu>
      <Menu.Item key="ancillaries" onClick={() => handleAddPlan('ancillaries')}>
        Add Ancillary Plan
      </Menu.Item>
      {!customerData.privateMed?.carrier && (
        <Menu.Item key="privateMed" onClick={() => handleAddPlan('privateMed')}>
          Add Private Medical Plan
        </Menu.Item>
      )}
      {!customerData.majorMed?.carrier && (
        <Menu.Item key="majorMed" onClick={() => handleAddPlan('majorMed')}>
          Add Major Medical Plan
        </Menu.Item>
      )}
    </Menu>
  );

  return (
    <Row gutter={[16, 16]} style={{ marginTop: '20px' }}>
      <Col sm={24} md={24} lg={24} xl={24}>
        <div
          className="form-heading"
          style={{ justifyContent: 'space-between', display: 'flex' }}
        >
          <span>Plans</span>
          <Dropdown overlay={planMenu} trigger={['click']}>
            <button
              type="button"
              style={{ marginLeft: '10px' }}
              className="add-ancillary-button"
            >
              <span>Add</span>
              <img src={plus} alt="plus icon" />
            </button>
          </Dropdown>
        </div>
      </Col>

      {/* Ancillary Plans */}
      {editableData.ancillaries.map((plan, index) => (
        <Col span={8} key={index}>
          <Card
            title="Ancillary Plan"
            extra={
              <>
                {editState.ancillaries[index] ? (
                  <>
                    <Button
                      onClick={() => handleSave('ancillaries', index)}
                      style={{ marginRight: '10px' }}
                    >
                      Save
                    </Button>
                    <Button
                      onClick={() => cancelEdit('ancillaries', index)}
                      style={{ marginRight: '10px' }}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button onClick={() => toggleEditMode('ancillaries', index)}>
                    Edit
                  </Button>
                )}
              </>
            }
          >
            <div style={{ padding: '10px' }} className="custo-input">
              {editState.ancillaries[index] ? (
                <>
                  <p>
                    <strong>Carrier:</strong>{' '}
                    <Input
                      value={editableData.ancillaries[index].carrier}
                      onChange={(e) =>
                        handleChange('ancillaries', index, 'carrier', e.target.value)
                      }
                    />
                  </p>
                  <p>
                    <strong>Is Post Date:</strong>{' '}
                    <Radio.Group
                      value={editableData.ancillaries[index].isPostDate ? 'Yes' : 'No'}
                      onChange={(e) =>
                        handleChange(
                          'ancillaries',
                          index,
                          'isPostDate',
                          e.target.value === 'Yes'
                        )
                      }
                    >
                      <Radio value="Yes">Yes</Radio>
                      <Radio value="No">No</Radio>
                    </Radio.Group>
                  </p>
                  <p>
                    <strong>
                      {editableData.ancillaries[index].isPostDate
                        ? 'Billing Date'
                        : 'Todays Date'}
                    </strong>{' '}
                    <DatePicker
                      style={{ width: '100%' }}
                      value={
                        editableData.ancillaries[index].billingDate
                          ? moment(editableData.ancillaries[index].billingDate)
                          : null
                      }
                      onChange={(date) =>
                        handleChange('ancillaries', index, 'billingDate', date ? date.format('MM/DD/YYYY') : '')
                      }
                      format="MM/DD/YYYY"
                    />
                  </p>
                  <p>
                    <strong>Premium:</strong>{' '}
                    <Input
                      value={editableData.ancillaries[index].premium}
                      onChange={(e) =>
                        handleChange('ancillaries', index, 'premium', e.target.value)
                      }
                    />
                  </p>
                  <p>
                    <strong>Status:</strong>{' '}
                    <Select
                      value={editableData.ancillaries[index].status}
                      onChange={(value) => handleChange('ancillaries', index, 'status', value)}
                      style={{ width: '100%' }}
                    >
                      {statusOptions.map((status) => (
                        <Select.Option key={status} value={status}>
                          {status}
                        </Select.Option>
                      ))}
                    </Select>
                  </p>
                </>
              ) : (
                <>
                  <p>
                    <strong>Carrier:</strong> {plan.carrier}
                  </p>
                  <p>
                    <strong>Billing Date:</strong>{' '}
                    {plan.billingDate ? moment.utc(plan.billingDate).format('MM/DD/YYYY') : 'N/A'}
                  </p>
                  <p>
                    <strong>Premium:</strong> ${plan.premium || 0}
                  </p>
                  <p>
                    <strong>Status:</strong>{' '}
                    <span style={getStatusStyle(plan.status)}>{plan.status || 'N/A'}</span>
                  </p>
                </>
              )}
            </div>
          </Card>
        </Col>
      ))}

      {/* Private Medical Plan */}
      {(editableData.privateMed?.carrier || editState.privateMed) && (
        <Col span={8}>
          <Card
            title="Private Medical Plan"
            extra={
              <>
                {editState.privateMed ? (
                  <>
                    <Button
                      onClick={() => handleSave('privateMed')}
                      style={{ marginRight: '10px' }}
                    >
                      Save
                    </Button>
                    <Button
                      onClick={() => cancelEdit('privateMed')}
                      style={{ marginRight: '10px' }}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button onClick={() => toggleEditMode('privateMed')}>Edit</Button>
                )}
              </>
            }
          >
            <div style={{ padding: '10px' }} className="custo-input">
              {editState.privateMed ? (
                <>
                  <p>
                    <strong>Carrier:</strong>{' '}
                    <Input
                      value={editableData.privateMed.carrier}
                      onChange={(e) => handleChange('privateMed', null, 'carrier', e.target.value)}
                    />
                  </p>
                  <p>
                    <strong>Is Post Date:</strong>{' '}
                    <Radio.Group
                      value={editableData.privateMed.isPostDate ? 'Yes' : 'No'}
                      onChange={(e) =>
                        handleChange('privateMed', null, 'isPostDate', e.target.value === 'Yes')
                      }
                    >
                      <Radio value="Yes">Yes</Radio>
                      <Radio value="No">No</Radio>
                    </Radio.Group>
                  </p>
                  <p>
                    <strong>
                      {editableData.privateMed.isPostDate ? 'Billing Date' : 'Todays Date'}
                    </strong>{' '}
                    <DatePicker
                      style={{ width: '100%' }}
                      value={
                        editableData.privateMed.billingDate
                          ? moment(editableData.privateMed.billingDate)
                          : null
                      }
                      onChange={(date) =>
                        handleChange('privateMed', null, 'billingDate', date ? date.format('MM/DD/YYYY') : '')
                      }
                      format="MM/DD/YYYY"
                    />
                  </p>
                  <p>
                    <strong>Premium:</strong>{' '}
                    <Input
                      value={editableData.privateMed.premium}
                      onChange={(e) => handleChange('privateMed', null, 'premium', e.target.value)}
                    />
                  </p>
                  <p>
                    <strong>Status:</strong>{' '}
                    <Select
                      value={editableData.privateMed.status}
                      onChange={(value) => handleChange('privateMed', null, 'status', value)}
                      style={{ width: '100%' }}
                    >
                      {statusOptions.map((status) => (
                        <Select.Option key={status} value={status}>
                          {status}
                        </Select.Option>
                      ))}
                    </Select>
                  </p>
                </>
              ) : (
                <>
                  <p>
                    <strong>Carrier:</strong> {customerData.privateMed?.carrier}
                  </p>
                  <p>
                    <strong>Billing Date:</strong>{' '}
                    {customerData.privateMed?.billingDate
                      ? moment.utc(customerData.privateMed.billingDate).format('MM/DD/YYYY')
                      : 'N/A'}
                  </p>
                  <p>
                    <strong>Premium:</strong> ${customerData.privateMed?.premium}
                  </p>
                  <p>
                    <strong>Status:</strong>{' '}
                    <span style={getStatusStyle(customerData.privateMed?.status)}>
                      {customerData.privateMed?.status || 'N/A'}
                    </span>
                  </p>
                </>
              )}
            </div>
          </Card>
        </Col>
      )}

      {/* Major Medical Plan */}
      {(editableData.majorMed?.carrier || editState.majorMed) && (
        <Col span={8}>
          <Card
            title="Major Medical Plan"
            extra={
              <>
                {editState.majorMed ? (
                  <>
                    <Button
                      onClick={() => handleSave('majorMed')}
                      style={{ marginRight: '10px' }}
                    >
                      Save
                    </Button>
                    <Button
                      onClick={() => cancelEdit('majorMed')}
                      style={{ marginRight: '10px' }}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button onClick={() => toggleEditMode('majorMed')}>Edit</Button>
                )}
              </>
            }
          >
            <div style={{ padding: '10px' }} className="custo-input">
              {editState.majorMed ? (
                <>
                  <p>
                    <strong>Carrier:</strong>{' '}
                    <Input
                      value={editableData.majorMed.carrier}
                      onChange={(e) => handleChange('majorMed', null, 'carrier', e.target.value)}
                    />
                  </p>
                  <p>
                    <strong>Is Post Date:</strong>{' '}
                    <Radio.Group
                      value={editableData.majorMed.isPostDate ? 'Yes' : 'No'}
                      onChange={(e) =>
                        handleChange('majorMed', null, 'isPostDate', e.target.value === 'Yes')
                      }
                    >
                      <Radio value="Yes">Yes</Radio>
                      <Radio value="No">No</Radio>
                    </Radio.Group>
                  </p>
                  <p>
                    <strong>
                      {editableData.majorMed.isPostDate ? 'Billing Date' : 'Todays Date'}:
                    </strong>
                    <DatePicker
                      style={{ width: '100%' }}
                      value={
                        editableData.majorMed.billingDate
                          ? moment(editableData.majorMed.billingDate)
                          : null
                      }
                      onChange={(date) =>
                        handleChange('majorMed', null, 'billingDate', date ? date.format('MM/DD/YYYY') : '')
                      }
                      format="MM/DD/YYYY"
                    />
                  </p>
                  <p>
                    <strong>Premium:</strong>{' '}
                    <Input
                      value={editableData.majorMed.premium}
                      onChange={(e) => handleChange('majorMed', null, 'premium', e.target.value)}
                    />
                  </p>
                  <p>
                    <strong>Status:</strong>{' '}
                    <Select
                      value={editableData.majorMed.status}
                      onChange={(value) => handleChange('majorMed', null, 'status', value)}
                      style={{ width: '100%' }}
                    >
                      {statusOptions.map((status) => (
                        <Select.Option key={status} value={status}>
                          {status}
                        </Select.Option>
                      ))}
                    </Select>
                  </p>
                </>
              ) : (
                <>
                  <p>
                    <strong>Carrier:</strong> {customerData.majorMed?.carrier}
                  </p>
                  <p>
                    <strong>Billing Date:</strong>{' '}
                    {customerData.majorMed?.billingDate
                      ? moment.utc(customerData.majorMed.billingDate).format('MM/DD/YYYY')
                      : 'N/A'}
                  </p>
                  <p>
                    <strong>Premium:</strong> ${customerData.majorMed?.premium}
                  </p>
                  <p>
                    <strong>Status:</strong>{' '}
                    <span style={getStatusStyle(customerData.majorMed?.status)}>
                      {customerData.majorMed?.status}
                    </span>
                  </p>
                </>
              )}
            </div>
          </Card>
        </Col>
      )}
    </Row>
  );
};

export default CustomerPlans;
